"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import {
  PawPrint,
  User,
  Home,
  FileText,
  FolderOpen,
  LogOut,
  ChevronDown,
  Calendar,
  Receipt,
  MessageCircle,
  Pill,
} from "lucide-react";
import PortalSchedulingAgent from "@/components/PortalSchedulingAgent";

export default function PortalLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [simpleMode, setSimpleMode] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load simple mode preference
  useEffect(() => {
    try {
      const saved = localStorage.getItem("petra_simple_mode");
      if (saved === "true") {
        setSimpleMode(true);
      }
      // Show onboarding prompt if no preference has been set
      const hasChosen = localStorage.getItem("petra_mode_chosen");
      if (!hasChosen && pathname === "/portal") {
        setShowOnboarding(true);
      }
    } catch {
      // ignore
    }
  }, [pathname]);

  function chooseMode(mode: "simple" | "full") {
    const isSimple = mode === "simple";
    setSimpleMode(isSimple);
    setShowOnboarding(false);
    try {
      localStorage.setItem("petra_simple_mode", String(isSimple));
      localStorage.setItem("petra_mode_chosen", "true");
    } catch {
      // ignore
    }
  }

  // Don't show layout chrome on login page
  if (pathname === "/portal/login") {
    return <>{children}</>;
  }

  async function handleLogout() {
    try {
      await fetch("/api/portal/auth/logout", { method: "POST" });
    } catch {
      // proceed to redirect regardless
    }
    window.location.href = "/portal/login";
  }

  // Full navigation items
  const fullNavItems = [
    { label: "Home", icon: Home, href: "/portal" },
    { label: "Appointments", icon: Calendar, href: "/portal/appointments" },
    { label: "Invoices", icon: Receipt, href: "/portal/invoices" },
    { label: "Messages", icon: MessageCircle, href: "/portal/messages" },
    { label: "Documents", icon: FolderOpen, href: "/portal/documents" },
  ];

  // Simple mode: 4 items only
  const simpleNavItems = [
    { label: "Home", icon: Home, href: "/portal" },
    { label: "Appointments", icon: Calendar, href: "/portal/appointments" },
    { label: "Messages", icon: MessageCircle, href: "/portal/messages" },
    { label: "Refills", icon: Pill, href: "/portal/refills" },
  ];

  const bottomNavItems = simpleMode ? simpleNavItems : fullNavItems;

  // Pages hidden in simple mode
  const hiddenInSimpleMode = [
    "/portal/estimates",
    "/portal/waitlist",
    "/portal/telemedicine",
    "/portal/boarding-reports",
  ];

  // Redirect away from hidden pages in simple mode
  useEffect(() => {
    if (simpleMode && hiddenInSimpleMode.some((p) => pathname.startsWith(p))) {
      router.push("/portal");
    }
  }, [simpleMode, pathname, router]);

  function isActive(href: string) {
    if (href === "/portal") {
      return pathname === "/portal";
    }
    return pathname.startsWith(href);
  }

  const simpleModeClass = simpleMode ? "text-xl" : "";

  return (
    <div className={`min-h-screen bg-slate-50 flex flex-col ${simpleMode ? "text-[20px]" : ""}`}>
      {/* Onboarding Mode Prompt */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-5">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto bg-[#c5a54e]/10 rounded-full flex items-center justify-center mb-3">
                <PawPrint className="w-7 h-7 text-[#c5a54e]" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Welcome to Petra!</h2>
              <p className="text-sm text-slate-500 mt-1">
                How would you like to use the portal?
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => chooseMode("simple")}
                className="w-full p-4 rounded-xl border-2 border-slate-200 hover:border-[#c5a54e] transition-colors text-left"
              >
                <p className="font-semibold text-slate-900">Simple Mode</p>
                <p className="text-xs text-slate-500 mt-1">
                  Just the essentials: appointments, messages, and refills. Larger text, fewer options.
                </p>
              </button>

              <button
                onClick={() => chooseMode("full")}
                className="w-full p-4 rounded-xl border-2 border-slate-200 hover:border-[#c5a54e] transition-colors text-left"
              >
                <p className="font-semibold text-slate-900">Full Experience</p>
                <p className="text-xs text-slate-500 mt-1">
                  Everything: documents, invoices, estimates, telemedicine, waitlist, and more.
                </p>
              </button>
            </div>

            <p className="text-[10px] text-slate-400 text-center">
              You can change this later in Profile &rarr; Preferences
            </p>
          </div>
        </div>
      )}

      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sticky top-0 z-50">
        {/* Left: Logo */}
        <button
          onClick={() => router.push("/portal")}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <PawPrint className="w-7 h-7 text-[#c5a54e]" />
          <span className="text-xl font-bold tracking-wider text-[#c5a54e]">
            Petra
          </span>
        </button>

        {/* Right: User Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-700"
          >
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
              <User className="w-4 h-4 text-slate-500" />
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  router.push("/portal/profile");
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <User className="w-4 h-4 text-slate-400" />
                Profile
              </button>
              {simpleMode && (
                <div className="px-4 py-1.5">
                  <span className="text-[10px] text-[#c5a54e] font-medium bg-[#c5a54e]/10 px-2 py-0.5 rounded-full">
                    Simple Mode
                  </span>
                </div>
              )}
              <div className="border-t border-slate-100 my-1" />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Log Out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className={`flex-1 max-w-4xl w-full mx-auto px-4 py-6 pb-24 md:pb-6 ${simpleMode ? "text-lg" : ""}`}>
        {children}
      </main>

      {/* Bottom Navigation (mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50">
        <div className="flex items-center justify-around h-16">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <button
                key={item.label}
                onClick={() => router.push(item.href)}
                className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${
                  active
                    ? "text-[#c5a54e]"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className={`font-medium ${simpleMode ? "text-xs" : "text-[10px]"}`}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* AI Scheduling Assistant — hidden in simple mode */}
      {!simpleMode && <PortalSchedulingAgent />}
    </div>
  );
}
